import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    google?: typeof google;
    __tfrGoogleMapsLoadPromise?: Promise<void>;
    gm_authFailure?: () => void;
  }
}

@Injectable({ providedIn: 'root' })
export class GoogleMapsLoaderService {
  isConfigured(): boolean {
    const key = environment.googleMapsApiKey?.trim() ?? '';
    return key.length > 0 && !key.includes('REPLACE_WITH');
  }

  load(): Promise<void> {
    if (typeof window === 'undefined') {
      return Promise.reject(new Error('Google Maps solo está disponible en el navegador'));
    }

    if (window.google?.maps?.places?.PlaceAutocompleteElement) {
      return Promise.resolve();
    }

    if (!this.isConfigured()) {
      return Promise.reject(new Error('Google Maps API key is not configured'));
    }

    if (!window.__tfrGoogleMapsLoadPromise) {
      window.__tfrGoogleMapsLoadPromise = this.#loadMapsAndPlaces().catch(err => {
        window.__tfrGoogleMapsLoadPromise = undefined;
        throw err;
      });
    }

    return window.__tfrGoogleMapsLoadPromise;
  }

  async #loadMapsAndPlaces(): Promise<void> {
    await this.#loadScript();
    await this.#ensurePlacesLibrary();
  }

  #loadScript(): Promise<void> {
    const key = environment.googleMapsApiKey.trim();
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src*="maps.googleapis.com/maps/api/js"]',
    );

    if (existing) {
      return this.#waitForGoogleMaps(existing);
    }

    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const fail = (message: string) => {
        if (settled) return;
        settled = true;
        reject(new Error(message));
      };

      const prevAuthFailure = window.gm_authFailure;
      window.gm_authFailure = () => {
        fail(
          'Google rechazó la API key (facturación, restricciones de referrer o APIs no activadas).',
        );
      };

      const script = document.createElement('script');
      script.async = true;
      script.defer = true;
      script.src =
        `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}` +
        '&v=weekly&loading=async';

      script.onload = () => {
        window.gm_authFailure = prevAuthFailure;
        this.#waitForGoogleMaps(script)
          .then(() => {
            if (!settled) {
              settled = true;
              resolve();
            }
          })
          .catch(err => fail(err instanceof Error ? err.message : String(err)));
      };

      script.onerror = () => {
        window.gm_authFailure = prevAuthFailure;
        fail('No se pudo descargar maps.googleapis.com (red, bloqueo o key inválida).');
      };

      document.head.appendChild(script);
    });
  }

  #waitForGoogleMaps(script: HTMLScriptElement): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const deadline = Date.now() + 15_000;

      const tick = () => {
        if (typeof window.google?.maps?.importLibrary === 'function') {
          resolve();
          return;
        }
        if (Date.now() > deadline) {
          script.remove();
          reject(new Error('Google Maps no respondió a tiempo tras cargar el script.'));
          return;
        }
        requestAnimationFrame(tick);
      };

      tick();
    });
  }

  async #ensurePlacesLibrary(): Promise<void> {
    if (typeof window.google?.maps?.importLibrary !== 'function') {
      throw new Error('google.maps.importLibrary no está disponible.');
    }

    await google.maps.importLibrary('places');

    if (!window.google.maps.places?.PlaceAutocompleteElement) {
      throw new Error(
        'La biblioteca Places no expuso PlaceAutocompleteElement. Activa "Maps JavaScript API" y "Places API (New)" en Google Cloud.',
      );
    }
  }
}
